"use client";

import { useState } from "react";
import { TimingHeatmap } from "./TimingHeatmap";
import { CrowdCalendar } from "./CrowdCalendar";
import type { LocationTimingData } from "@/lib/conditions/timing-data";

type Props = {
  primary: LocationTimingData;
  alternate: LocationTimingData | null;
};

export function BestTimeModule({ primary, alternate }: Props) {
  const [showAlternate, setShowAlternate] = useState(false);
  const data = showAlternate && alternate ? alternate : primary;

  return (
    <section className="max-w-[1100px] mx-auto px-5 md:px-12 py-12 md:py-16">
      <div className="text-center mb-8">
        <div className="text-[11px] font-semibold uppercase tracking-[2px] text-gold mb-2">
          Planning tool
        </div>
        <h2 className="font-serif text-[22px] md:text-[28px] font-bold text-navy tracking-tight">
          Best time to visit {data.name}
        </h2>
        <p className="text-[14px] text-text-muted mt-2 max-w-[560px] mx-auto">
          Combining historical conditions with school holiday crowd pressure to find the sweet spot.
        </p>
      </div>

      {data.insights.length > 0 && (
        <div className="bg-gold/[0.06] border border-gold/20 rounded-[14px] p-5 md:p-6 mb-8">
          <h3 className="text-[12px] font-semibold uppercase tracking-[1.2px] text-gold mb-3">
            Key insights
          </h3>
          <ul className="space-y-2">
            {data.insights.map((insight, i) => (
              <li key={i} className="flex gap-3 items-start text-[14px] leading-[1.55] text-text-body">
                <span aria-hidden className="mt-[7px] shrink-0 w-1.5 h-1.5 rounded-full bg-gold" />
                <span>{insight}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-white rounded-[20px] border border-border-light p-5 md:p-8 mb-8">
        <TimingHeatmap
          weeks={data.weeks}
          seasonLabel={data.season}
          alternateSeasonLabel={alternate && !showAlternate ? alternate.season : showAlternate ? primary.season : undefined}
          onToggleSeason={alternate ? () => setShowAlternate(!showAlternate) : undefined}
        />
      </div>

      {data.feederHolidays.length > 0 && (
        <div className="bg-white rounded-[20px] border border-border-light p-5 md:p-8 mb-8">
          <CrowdCalendar feederHolidays={data.feederHolidays} />
        </div>
      )}

      <details className="bg-warm-bg rounded-[14px] p-5 md:p-6">
        <summary className="flex items-center justify-between cursor-pointer list-none text-[14px] font-semibold text-navy">
          <span>How are these scores calculated?</span>
          <span aria-hidden className="text-gold text-xl group-open:rotate-45 transition-transform">+</span>
        </summary>
        <div className="mt-3 text-[13px] leading-[1.7] text-text-body space-y-2">
          <p>
            The timing score combines two signals: historical conditions quality
            (how good the skiing or surfing typically is in a given week, based
            on 5 years of weather data) and crowd pressure (how many of this
            destination&apos;s feeder markets have school holidays that week).
          </p>
          <p>
            Crowd pressure is weighted by each feeder country&apos;s share of
            visitors. If 40% of a resort&apos;s visitors come from France and
            France is on holiday, that contributes 0.40 to the crowd pressure
            score. Crowds can reduce the timing score by up to 35%, ensuring
            conditions still matter most.
          </p>
          <p>
            Scores: 5 = great conditions with low crowds (the sweet spot).
            4 = great conditions with moderate crowds, or good conditions with
            low crowds. 3 = average. 2 = below average conditions or very
            crowded. 1 = poor conditions or peak holiday chaos.
          </p>
        </div>
      </details>
    </section>
  );
}
